import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEvents, EventData } from '../contexts/EventsContext';
import { useUsers } from '../contexts/UsersContext';
import { useToast } from '../contexts/ToastContext';
import EventModal from './EventModal';
import ConfirmationModal from './ConfirmationModal';
import UserPromotionModal from './UserPromotionModal';
import { formatDateTimeBR, debugTimezoneFunctions } from '../utils/dateUtils';
import { 
  Users, 
  Calendar, 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Shield, 
  Clock,
  MapPin,
  User,
  Tag,
  Search,
  Filter,
  Crown,
  UserCheck,
  UserX,
  Mail,
  Calendar as CalendarIcon,
  Truck,
  GraduationCap,
  RotateCcw,
  Trophy
} from 'lucide-react';
import { AdminMasterShopTab } from './AdminMasterShopTab';

// Tooltip instantâneo flutuante no hover (CSS puro com Tailwind)
const ActionTooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => (
  <div className="relative group/tooltip inline-flex items-center justify-center">
    {children}
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 text-xs font-medium text-white bg-[#0a0b0d] border border-gray-700/80 rounded-lg shadow-2xl opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-30">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-[#0a0b0d]" />
    </div>
  </div>
);

interface AdminPanelProps {
  currentView?: string;
  onViewChange?: (view: string) => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ 
  currentView,
  onViewChange
}) => {
  const { isAdmin, profile } = useAuth();
  const { showToast } = useToast();
  const { events, loading, deleteEvent, testConnection } = useEvents();
  const { 
    users, 
    loading: usersLoading, 
    error: usersError,
    promoteToAdmin, 
    demoteAdmin, 
    promoteToMentor,
    demoteMentor,
    toggleUserStatus,
    approveSupplier,
    demoteSupplier
  } = useUsers();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  
  // States para modal de confirmação
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<EventData | null>(null);

  // States para gestão de usuários
  const [userFilter, setUserFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [isUserPromotionModalOpen, setIsUserPromotionModalOpen] = useState(false);
  const [userActionLoading, setUserActionLoading] = useState<string | null>(null);

  // Sincronizar activeTab interno com currentView externo apenas uma vez na inicialização
  useEffect(() => {
    if (currentView) {
      if (currentView === 'users:pending') {
        setActiveTab('users');
        setUserFilter('pending_fornecedor');
      } else if (currentView !== activeTab) {
        setActiveTab(currentView);
      }
    }
  }, [currentView]); // Removido activeTab da dependência para evitar loop

  // Notificar mudanças de activeTab para o componente pai apenas quando mudar internamente
  const notifyViewChange = useCallback((newTab: string) => {
    if (onViewChange && newTab !== currentView) {
      onViewChange(newTab);
    }
  }, [onViewChange, currentView]);

  // Atualizar activeTab interno e notificar pai
  const updateActiveTab = useCallback((newTab: string) => {
    setActiveTab(newTab);
    notifyViewChange(newTab);
  }, [notifyViewChange]);

  // Redirecionar se não for admin
  if (!isAdmin) {
    return (
      <div className="flex-1 bg-[#121316] p-8 pt-24">
        <div className="text-center text-white">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">Acesso Negado</h2>
          <p className="text-gray-400">Você não tem permissão para acessar esta área.</p>
        </div>
      </div>
    );
  }

  // Funções para manipular eventos
  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setIsEventModalOpen(true);
  };

  const handleEditEvent = (event: EventData) => {
    setSelectedEvent(event);
    setIsEventModalOpen(true);
  };

  // Iniciar processo de exclusão (abre modal de confirmação)
  const handleDeleteEvent = (event: EventData) => {
    setEventToDelete(event);
    setIsConfirmModalOpen(true);
  };

  // Confirmar exclusão (executar quando usuário confirma no modal)
  const confirmDeleteEvent = async () => {
    if (!eventToDelete) return;

    setDeleteLoading(eventToDelete.id);
    try {
      console.log('🗑️ Administrador confirmou exclusão:', eventToDelete.title);
      const result = await deleteEvent(eventToDelete.id);
      if (!result.success) {
        console.error('❌ Falha ao excluir:', result.error);
        showToast({
          type: 'error',
          title: 'Erro ao Excluir',
          message: `Erro ao excluir evento: ${result.error}`
        });
      } else {
        console.log('✅ Evento excluído com sucesso!');
        showToast({
          type: 'success',
          title: 'Evento Excluído!',
          message: 'O evento foi removido com sucesso.'
        });
      }
    } catch (error) {
      console.error('💥 Erro inesperado:', error);
      showToast({
        type: 'error',
        title: 'Erro Inesperado',
        message: 'Erro inesperado ao excluir evento'
      });
    } finally {
      setDeleteLoading(null);
      setIsConfirmModalOpen(false);
      setEventToDelete(null);
    }
  };

  // Cancelar exclusão
  const cancelDeleteEvent = () => {
    setIsConfirmModalOpen(false);
    setEventToDelete(null);
  };

  // Funções para gestão de usuários
  const handlePromoteUser = async (userEmail: string) => {
    setUserActionLoading(userEmail);
    try {
      const result = await promoteToAdmin(userEmail);
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Usuário Promovido!',
          message: result.message
        });
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Promover',
          message: result.message
        });
      }
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Erro Inesperado',
        message: 'Erro inesperado ao promover usuário'
      });
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleDemoteAdmin = async (userEmail: string) => {
    setUserActionLoading(userEmail);
    try {
      const result = await demoteAdmin(userEmail);
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Admin Rebaixado!',
          message: result.message
        });
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Rebaixar',
          message: result.message
        });
      }
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Erro Inesperado',
        message: 'Erro inesperado ao rebaixar admin'
      });
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleToggleUserStatus = async (userEmail: string) => {
    setUserActionLoading(userEmail);
    try {
      const result = await toggleUserStatus(userEmail);
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Status Alterado!',
          message: result.message
        });
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Alterar Status',
          message: result.message
        });
      }
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Erro Inesperado',
        message: 'Erro inesperado ao alterar status do usuário'
      });
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleApproveSupplier = async (userEmail: string) => {
    setUserActionLoading(userEmail);
    try {
      const result = await approveSupplier(userEmail);
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Fornecedor Aprovado!',
          message: result.message
        });
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Aprovar',
          message: result.message
        });
      }
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Erro Inesperado',
        message: 'Erro inesperado ao aprovar fornecedor'
      });
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleDemoteSupplier = async (userEmail: string) => {
    setUserActionLoading(userEmail);
    try {
      const result = await demoteSupplier(userEmail);
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Fornecedor Rebaixado!',
          message: result.message
        });
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Rebaixar',
          message: result.message
        });
      }
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Erro Inesperado',
        message: 'Erro inesperado ao rebaixar fornecedor'
      });
    } finally {
      setUserActionLoading(null);
    }
  };

  const handlePromoteMentor = async (userEmail: string) => {
    setUserActionLoading(userEmail);
    try {
      const result = await promoteToMentor(userEmail);
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Mentor Promovido!',
          message: result.message
        });
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Promover',
          message: result.message
        });
      }
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Erro Inesperado',
        message: 'Erro inesperado ao promover mentor'
      });
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleDemoteMentor = async (userEmail: string) => {
    setUserActionLoading(userEmail);
    try {
      const result = await demoteMentor(userEmail);
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Mentor Rebaixado!',
          message: result.message
        });
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Rebaixar',
          message: result.message
        });
      }
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Erro Inesperado',
        message: 'Erro inesperado ao rebaixar mentor'
      });
    } finally {
      setUserActionLoading(null);
    }
  };

  // Filtrar usuários
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
                         (user.full_name && user.full_name.toLowerCase().includes(userSearch.toLowerCase()));
    
    const matchesFilter = userFilter === 'all' || 
                         (userFilter === 'admin' && user.role === 'admin') ||
                         (userFilter === 'mentor' && user.role === 'mentor') ||
                         (userFilter === 'user' && user.role === 'user') ||
                         (userFilter === 'fornecedor' && user.role === 'fornecedor') ||
                         (userFilter === 'pending_fornecedor' && user.is_supplier_requested && user.role !== 'fornecedor');
    
    return matchesSearch && matchesFilter;
  });

  const getEventLabelStyle = (labelColor: string) => {
    const isLightColor = labelColor === '#0FFF55' || labelColor.toLowerCase().includes('fff') || labelColor.toLowerCase().includes('yellow');
    return {
      backgroundColor: labelColor,
      color: isLightColor ? '#121316' : '#ffffff'
    };
  };

  const tabs = [
    { id: 'overview', label: 'Visão Geral', icon: Settings },
    { id: 'events', label: 'Eventos', icon: Calendar },
    { id: 'users', label: 'Usuários', icon: Users },
    { id: 'mastershop', label: 'Faturamento Master Shop', icon: Trophy },
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-[#1a1d21] rounded-3xl p-6 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total de Eventos</p>
              <p className="text-2xl font-bold text-white">{events.length}</p>
            </div>
            <Calendar className="w-8 h-8 text-[#0FFF55]" />
          </div>
        </div>

        <div className="bg-[#1a1d21] rounded-3xl p-6 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Usuários Ativos</p>
              <p className="text-2xl font-bold text-white">{users.filter(u => u.is_active).length}</p>
            </div>
            <Users className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-[#1a1d21] rounded-3xl p-6 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Administradores</p>
              <p className="text-2xl font-bold text-white">{users.filter(u => u.role === 'admin' && u.is_active).length}</p>
            </div>
            <Shield className="w-8 h-8 text-red-400" />
          </div>
        </div>

        <div className="bg-[#1a1d21] rounded-3xl p-6 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Usuários Suspensos</p>
              <p className="text-2xl font-bold text-white">{users.filter(u => !u.is_active).length}</p>
            </div>
            <UserX className="w-8 h-8 text-orange-400" />
          </div>
        </div>
      </div>

      <div className="bg-[#1a1d21] rounded-3xl p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-white mb-4">Atividades Recentes</h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-4 p-4 bg-[#121316] rounded-2xl">
            <div className="w-10 h-10 bg-[#0FFF55] rounded-full flex items-center justify-center">
              <Plus className="w-5 h-5 text-[#121316]" />
            </div>
            <div>
              <p className="text-white font-semibold">Novo evento criado</p>
              <p className="text-gray-400 text-sm">Call Semanal - Estratégias de Vendas adicionado há 2 horas</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 p-4 bg-[#121316] rounded-2xl">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold">Novo usuário registrado</p>
              <p className="text-gray-400 text-sm">João Silva se cadastrou na plataforma há 1 hora</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEvents = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-white">Gerenciar Eventos</h3>
        <div className="flex space-x-3">
          <button 
            onClick={testConnection}
            className="bg-green-600 text-white font-bold px-4 py-3 rounded-2xl hover:bg-green-700 transition-all duration-300 text-sm"
            title="RLS DESABILITADO - Executar teste de conexão (ver console)"
          >
            🔧 Debug (RLS OFF)
          </button>
          <button 
            onClick={debugTimezoneFunctions}
            className="bg-blue-600 text-white font-bold px-4 py-3 rounded-2xl hover:bg-blue-700 transition-all duration-300 text-sm"
            title="Testar conversões de timezone (ver console)"
          >
            🕒 Teste Timezone
          </button>
          <button 
            onClick={handleCreateEvent}
            className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold px-6 py-3 rounded-2xl hover:shadow-lg hover:shadow-[#0FFF55]/30 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>Novo Evento</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-[#0FFF55] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando eventos...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h4 className="text-xl font-bold text-white mb-2">Nenhum evento criado</h4>
          <p className="text-gray-400 mb-6">
            Comece criando seu primeiro evento para a comunidade.
          </p>
          <button 
            onClick={handleCreateEvent}
            className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold px-6 py-3 rounded-2xl hover:shadow-lg hover:shadow-[#0FFF55]/30 transition-all duration-300"
          >
            Criar Primeiro Evento
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {events.map((event) => {
            const createdDate = new Date(event.created_at);
            return (
              <div key={event.id} className="bg-[#1a1d21] rounded-3xl p-6 border border-gray-700 hover:border-[#0FFF55] transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-white mb-2">{event.title}</h4>
                    {event.description && (
                      <p className="text-gray-400 text-sm line-clamp-2 mb-2">{event.description}</p>
                    )}
                    
                    {/* Informações de criação */}
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <User size={12} />
                        <span>
                          {event.created_by_profile?.full_name || event.created_by_profile?.email || 'Admin'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock size={12} />
                        <span>
                          {createdDate.toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 ml-4">
                    <button 
                      onClick={() => handleEditEvent(event)}
                      className="p-2 text-gray-400 hover:text-[#0FFF55] hover:bg-[#0FFF55]/10 rounded-lg transition-colors"
                      title="Editar evento"
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      onClick={() => handleDeleteEvent(event)}
                      disabled={deleteLoading === event.id}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                      title="Excluir evento"
                    >
                      {deleteLoading === event.id ? (
                        <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-center space-x-2 text-gray-400">
                    <Clock size={14} />
                    <span>
                      {formatDateTimeBR(event.event_date)}
                    </span>
                    <span className="text-gray-500">
                      ({event.duration_minutes} min)
                    </span>
                  </div>
                  
                  {event.location && (
                    <div className="flex items-center space-x-2 text-gray-400">
                      <MapPin size={14} />
                      <span>{event.location}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Tag size={14} className="text-gray-400" />
                      <span 
                        className="px-3 py-1 rounded-full text-xs font-semibold"
                        style={getEventLabelStyle(event.label_color)}
                      >
                        {event.custom_label}
                      </span>
                    </div>
                    {event.participants_limit && (
                      <span className="text-gray-400 text-xs">
                        Máx. {event.participants_limit} participantes
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-white">Gerenciar Usuários</h3>
        <div className="flex space-x-4">
          <button
            onClick={() => setIsUserPromotionModalOpen(true)}
            className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold px-6 py-3 rounded-2xl hover:shadow-lg hover:shadow-[#0FFF55]/30 transition-all duration-300 flex items-center space-x-2"
          >
            <Crown size={20} />
            <span>Adicionar Admin</span>
          </button>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-[#1a1d21] rounded-3xl p-6 border border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="w-full bg-[#121316] border border-gray-600 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:border-[#0FFF55] focus:outline-none"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="text-gray-400" size={20} />
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="bg-[#121316] text-white border border-gray-600 rounded-xl px-4 py-3 focus:border-[#0FFF55] focus:outline-none"
            >
              <option value="all">Todos os usuários</option>
              <option value="admin">Administradores</option>
              <option value="mentor">Mentores</option>
              <option value="user">Usuários comuns</option>
              <option value="fornecedor">Fornecedores</option>
              <option value="pending_fornecedor">Solicitações de Fornecedores</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Usuários */}
      {usersLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-[#0FFF55] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando usuários...</p>
        </div>
      ) : usersError ? (
        <div className="text-center py-12">
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6">
            <p className="text-red-400">Erro ao carregar usuários: {usersError}</p>
          </div>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h4 className="text-xl font-bold text-white mb-2">Nenhum usuário encontrado</h4>
          <p className="text-gray-400">
            {userSearch || userFilter !== 'all' 
              ? 'Tente ajustar os filtros de busca.'
              : 'Ainda não há usuários cadastrados na plataforma.'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredUsers.map((user) => (
            <div key={user.id} className="bg-[#1a1d21] rounded-3xl p-6 border border-gray-700 hover:border-[#0FFF55] transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    user.role === 'admin' 
                      ? 'bg-red-500' 
                      : user.role === 'mentor'
                      ? 'bg-cyan-500'
                      : user.role === 'fornecedor' 
                      ? 'bg-purple-600' 
                      : 'bg-[#0FFF55]'
                  }`}>
                    {user.role === 'admin' ? (
                      <Crown className="w-6 h-6 text-white" />
                    ) : user.role === 'mentor' ? (
                      <GraduationCap className="w-6 h-6 text-white" />
                    ) : user.role === 'fornecedor' ? (
                      <Truck className="w-6 h-6 text-white" />
                    ) : (
                      <User className="w-6 h-6 text-[#121316]" />
                    )}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white">
                      {user.full_name || 'Usuário sem nome'}
                    </h4>
                    <p className="text-gray-400 text-sm">{user.email}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        user.role === 'admin' 
                          ? 'bg-red-500/20 text-red-400' 
                          : user.role === 'mentor'
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : user.role === 'fornecedor'
                          ? 'bg-purple-500/20 text-purple-400'
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {user.role === 'admin' ? 'Administrador' : user.role === 'mentor' ? 'Mentor' : user.role === 'fornecedor' ? 'Fornecedor' : 'Usuário'}
                      </span>
                      {user.is_supplier_requested && user.role !== 'fornecedor' && (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-400 animate-pulse">
                          Solicitou Fornecedor
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        user.is_active 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-orange-500/20 text-orange-400'
                      }`}>
                        {user.is_active ? 'Ativo' : 'Suspenso'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-1.5 items-center">
                  {/* Administrador */}
                  {user.role === 'admin' ? (
                    <ActionTooltip text="👤 Rebaixar Administrador">
                      <button
                        onClick={() => handleDemoteAdmin(user.email)}
                        disabled={userActionLoading === user.email}
                        className="p-2 text-gray-400 hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors disabled:opacity-50"
                        aria-label="Rebaixar Administrador"
                      >
                        {userActionLoading === user.email ? (
                          <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <User size={16} />
                        )}
                      </button>
                    </ActionTooltip>
                  ) : (
                    <ActionTooltip text="👑 Promover a Administrador">
                      <button
                        onClick={() => handlePromoteUser(user.email)}
                        disabled={userActionLoading === user.email}
                        className="p-2 text-gray-400 hover:text-[#0FFF55] hover:bg-[#0FFF55]/10 rounded-lg transition-colors disabled:opacity-50"
                        aria-label="Promover a Administrador"
                      >
                        {userActionLoading === user.email ? (
                          <div className="w-4 h-4 border-2 border-[#0FFF55] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Crown size={16} />
                        )}
                      </button>
                    </ActionTooltip>
                  )}

                  {/* Mentor */}
                  {user.role === 'mentor' ? (
                    <ActionTooltip text="🔄 Rebaixar Mentor">
                      <button
                        onClick={() => handleDemoteMentor(user.email)}
                        disabled={userActionLoading === user.email}
                        className="p-2 text-cyan-400 hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors disabled:opacity-50"
                        aria-label="Rebaixar Mentor"
                      >
                        {userActionLoading === user.email ? (
                          <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <RotateCcw size={16} />
                        )}
                      </button>
                    </ActionTooltip>
                  ) : user.role === 'user' ? (
                    <ActionTooltip text="🎓 Promover a Mentor">
                      <button
                        onClick={() => handlePromoteMentor(user.email)}
                        disabled={userActionLoading === user.email}
                        className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors disabled:opacity-50"
                        aria-label="Promover a Mentor"
                      >
                        {userActionLoading === user.email ? (
                          <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <GraduationCap size={16} />
                        )}
                      </button>
                    </ActionTooltip>
                  ) : null}

                  {/* Fornecedor */}
                  {user.role === 'fornecedor' ? (
                    <ActionTooltip text="👤 Rebaixar Fornecedor">
                      <button
                        onClick={() => handleDemoteSupplier(user.email)}
                        disabled={userActionLoading === user.email}
                        className="p-2 text-gray-400 hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors disabled:opacity-50"
                        aria-label="Rebaixar Fornecedor"
                      >
                        {userActionLoading === user.email ? (
                          <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <UserCheck size={16} />
                        )}
                      </button>
                    </ActionTooltip>
                  ) : (
                    user.role !== 'admin' && (
                      <ActionTooltip text={user.is_supplier_requested ? "🚚 Aprovar Fornecedor" : "🚚 Tornar Fornecedor"}>
                        <button
                          onClick={() => handleApproveSupplier(user.email)}
                          disabled={userActionLoading === user.email}
                          className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                            user.is_supplier_requested 
                              ? 'text-yellow-400 bg-yellow-500/10 hover:bg-yellow-500/20' 
                              : 'text-gray-400 hover:text-purple-400 hover:bg-purple-500/10'
                          }`}
                          aria-label="Aprovar Fornecedor"
                        >
                          {userActionLoading === user.email ? (
                            <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Truck size={16} />
                          )}
                        </button>
                      </ActionTooltip>
                    )
                  )}
                  
                  {/* Status Ativo/Suspenso */}
                  <ActionTooltip text={user.is_active ? '🚫 Suspender Usuário' : '✅ Reativar Usuário'}>
                    <button
                      onClick={() => handleToggleUserStatus(user.email)}
                      disabled={userActionLoading === user.email}
                      className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                        user.is_active 
                          ? 'text-gray-400 hover:text-red-400 hover:bg-red-500/10' 
                          : 'text-orange-400 hover:text-green-400 hover:bg-green-500/10'
                      }`}
                      aria-label={user.is_active ? 'Suspender Usuário' : 'Reativar Usuário'}
                    >
                      {userActionLoading === user.email ? (
                        <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                      ) : user.is_active ? (
                        <UserX size={16} />
                      ) : (
                        <UserCheck size={16} />
                      )}
                    </button>
                  </ActionTooltip>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <CalendarIcon size={14} />
                  <span>Membro desde {new Date(user.created_at).toLocaleDateString('pt-BR')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail size={14} />
                  <span>ID: {user.id.slice(0, 8)}...</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'events':
        return renderEvents();
      case 'users':
        return renderUsers();
      case 'mastershop':
        return <AdminMasterShopTab />;
      default:
        return renderOverview();
    }
  };

  return (
    <div className="flex-1 bg-[#121316] min-h-screen pt-20">
      {/* Header */}
      <div className="relative bg-gradient-to-br from-[#1a1d21] via-[#121316] to-[#0f1114] border-b border-gray-800">
        <div className="relative p-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-6">
              <h1 className="text-4xl font-bold text-white mb-2">
                Painel <span className="text-[#0FFF55]">Administrativo</span>
              </h1>
              <p className="text-gray-300 text-lg">
                Bem-vindo, <span className="text-[#0FFF55] font-semibold">{profile?.full_name || 'Admin'}</span>! 
                Gerencie a plataforma MasterClub.
              </p>
            </div>

            {/* Navigation Tabs */}
            <div className="flex justify-center">
              <div className="flex space-x-2 bg-[#1a1d21] p-2 rounded-2xl border border-gray-700">
                {tabs.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => updateActiveTab(id)}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-xl transition-all duration-300 ${
                      activeTab === id
                        ? 'bg-[#0FFF55] text-[#121316] shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-[#2a2d31]'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-8 max-w-7xl mx-auto">
        {renderContent()}
      </div>

      {/* Event Modal */}
      <EventModal
        isOpen={isEventModalOpen}
        onClose={() => {
          setIsEventModalOpen(false);
          setSelectedEvent(null);
        }}
        event={selectedEvent}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={cancelDeleteEvent}
        onConfirm={confirmDeleteEvent}
        title="Excluir Evento"
        message={`Tem certeza que deseja excluir o evento "${eventToDelete?.title}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteLoading === eventToDelete?.id}
        type="danger"
      />

      {/* User Promotion Modal */}
      <UserPromotionModal
        isOpen={isUserPromotionModalOpen}
        onClose={() => setIsUserPromotionModalOpen(false)}
        onSuccess={(message) => showToast({
          type: 'success',
          title: 'Sucesso!',
          message: message
        })}
      />


    </div>
  );
};

export default AdminPanel;